import React, { useEffect } from "react";
import { MapContainer, TileLayer, Marker, Popup, GeoJSON, useMapEvents } from "react-leaflet";
import "leaflet.fullscreen/Control.FullScreen.css";
import "leaflet.fullscreen";
import "leaflet-draw/dist/leaflet.draw.css";
import "leaflet-draw";
import "leaflet-gesture-handling/dist/leaflet-gesture-handling.css";
import "leaflet-gesture-handling";
import atriGeoJSON from "../../assets/atri.json";
import { getAllPins, createPin, deletePin, updatePin } from "../../services/pinService.js";
import { showErrorToast, showSuccessToast } from "../../services/toastService.js";
import { getAllSensors } from "../../services/sensorsService.js";
import { groupSensorsByPosition } from "../../utils/sensors.js";
import AddPinModal from "../modals/AddPinModal.jsx";
import ConfirmModal from "../modals/ConfirmModal.jsx";
import LoadingSpinner from "../LoadingSpinner.jsx";
import { useAuth } from "../../contexts/AuthContextProvider.jsx";
import { useLocation } from "react-router-dom";
import { useSensorsMap } from "../../contexts/SensorsMapProvider.jsx";
import SensorMarker from "./SensorMarker.jsx";
import PinPopup from "./PinPopup.jsx";
import FilterBar from "./FilterBar.jsx";
import L from "leaflet";
import LogoOverlay from "./LogoOverlay.jsx";
import SensorGraphOverlay from "./SensorGraphOverlay.jsx";

const SENSORI_GROUP_ID = process.env.ID_PIN_GROUP_SENSORI || 11;
const USER_LOCATION_GROUP_ID = process.env.ID_PIN_GROUP_UTENTI || 20;

export default function Map({ isMonitor = false }) {
	const location = useLocation();
	const canEdit = location.pathname === "/map";

	const { state, dispatch, ACTIONS } = useSensorsMap();
	const { user } = useAuth();

	useEffect(() => {
		if (navigator.geolocation) {
			navigator.geolocation.getCurrentPosition(
				(position) => {
					const { latitude, longitude } = position.coords;
					dispatch({ type: ACTIONS.SET_USER_LOCATION, payload: [latitude, longitude] });
				},
				(error) => {
					console.error("Errore nell'ottenere la posizione dell'utente:", error);
				}
			);
		}
	}, [ACTIONS, dispatch]);

	useEffect(() => {
		if (atriGeoJSON.features && atriGeoJSON.features.length > 0) {
			const center = atriGeoJSON.features.find(
				(feature) => feature.geometry.type === "Point"
			);
			const offset = [0, 0.01];
			if (center)
				dispatch({
					type: ACTIONS.SET_COORDS_ATRI,
					payload: [
						center.geometry.coordinates[1] + offset[0],
						center.geometry.coordinates[0] + offset[1],
					],
				});
		}

		const fetchMapPins = () => {
			getAllSensors()
				.then((sensors) => {
					const grouped = groupSensorsByPosition(sensors.data.data);
					dispatch({ type: ACTIONS.SET_SENSOR_GROUPS, payload: grouped });
				})
				.catch(() => showErrorToast("Errore nel caricamento dei sensori"));

			getAllPins()
				.then((response) => {
					dispatch({ type: ACTIONS.SET_PINS, payload: response.data.pins });
					dispatch({ type: ACTIONS.SET_PINS_GROUPS, payload: response.data.groups });
				})
				.catch(() => showErrorToast("Errore nel caricamento dei pin"));
		};

		fetchMapPins();
		const interval = setInterval(fetchMapPins, 5 * 60 * 1000);
		return () => clearInterval(interval);
	}, [user, ACTIONS, dispatch]);

	const addPin = (e) => {
		if (!state.addPinEnabled) return;

		dispatch({
			type: ACTIONS.SET_PENDING_PIN,
			payload: {
				lat: e.latlng.lat,
				lon: e.latlng.lng,
				title: "",
				html: "",
			},
		});
	};

	const savePin = async (pinData) => {
		dispatch({ type: ACTIONS.TOGGLE_MODAL, payload: false });

		if (pinData.id) {
			// Aggiorna un pin esistente
			try {
				await updatePin(pinData);
				pinData.updated_by = user.username;
				dispatch({
					type: ACTIONS.SET_PINS,
					payload: state.pins.map((pin) => (pin.id === pinData.id ? pinData : pin)),
				});
				showSuccessToast("Pin aggiornato con successo!");
			} catch {
				showErrorToast("Errore durante l'aggiornamento del pin.");
			}
		} else {
			const newPin = { ...state.pendingPin, ...pinData };

			createPin(newPin)
				.then((response) => {
					newPin.id = response.data.id;
					newPin.created_by = user.username;
					dispatch({
						type: ACTIONS.SET_PINS,
						payload: [...state.pins, { ...newPin }],
					});
					showSuccessToast("Pin aggiunto con successo!");
				})
				.catch(() => showErrorToast("Errore durante l'aggiunta del pin."));
		}
	};

	const cancelPin = () => {
		dispatch({ type: ACTIONS.TOGGLE_MODAL, payload: false });
	};

	const editPin = (pin) => {
		dispatch({ type: ACTIONS.SET_PENDING_PIN, payload: pin });
	};

	const confirmRemovePin = async () => {
		try {
			await deletePin(state.pinToDelete);
			dispatch({
				type: ACTIONS.SET_PINS,
				payload: state.pins.filter((pin) => pin.id !== state.pinToDelete),
			});
			showSuccessToast("Pin rimosso con successo!");
		} catch {
			showErrorToast("Errore durante la rimozione del pin.");
		}
		dispatch({ type: ACTIONS.SET_PIN_TO_DELETE, payload: null });
	};

	const MapClickHandler = () => {
		useMapEvents({
			click: addPin,
		});
		return null;
	};
	const MapClickHandlerSimple = () => {
		const [popup, setPopup] = React.useState(null);
		const map = useMapEvents({
			click: (e) => {
				const { lat, lng } = e.latlng;
				const googleMapsLink = `https://www.google.com/maps?q=${lat},${lng}`;

				// Funzione per convertire le coordinate in gradi, primi e secondi
				const toDMS = (decimal) => {
					const degrees = Math.floor(decimal);
					const minutesFloat = (decimal - degrees) * 60;
					const minutes = Math.floor(minutesFloat);
					const seconds = ((minutesFloat - minutes) * 60).toFixed(2);
					return `${degrees}° ${minutes}' ${seconds}"`;
				};

				const latDMS = toDMS(lat);
				const lngDMS = toDMS(lng);

				if (popup) {
					popup.remove();
				}

				const newPopup = L.popup()
					.setLatLng(e.latlng)
					.setContent(
						`<div>
								<p><strong>Coordinate Decimali:</strong><br> ${lat.toFixed(6)}, ${lng.toFixed(6)}</p>
								<p><strong>Coordinate DMS:</strong><br> ${latDMS} N, ${lngDMS} E</p>
								<a href="${googleMapsLink}" target="_blank" rel="noopener noreferrer">Apri in Google Maps</a>
							</div>`
					)
					.openOn(map);

				setPopup(newPopup);
			},
		});

		return null;
	};

	const handleMarkerClick = (group) => {
		if (!state.selectedType) return;
		dispatch({
			type: ACTIONS.SET_SELECTED_SENSOR,
			payload: group.sensors.filter((sensor) => sensor.type === state.selectedType)[0],
		});
	};

	if (!state.coordsAtri) return <LoadingSpinner />;

	return (
		<div className="max-h-[100vh] w-full h-full flex flex-col">
			<MapContainer
				center={state.coordsAtri}
				zoom={isMonitor ? 12 : 13}
				fullscreenControl={true}
				className="!rounded-lg"
				gestureHandling={!canEdit}
			>
				<TileLayer url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png" />
				<GeoJSON
					data={{
						...atriGeoJSON,
						features: atriGeoJSON.features.filter(
							(feature) => feature.geometry.type !== "Point"
						),
					}}
					style={{ color: "red", weight: 2, fillOpacity: 0.0 }}
				/>

				{/* Marker dei sensori */}
				{state.pinsGroups.some(
					(group) => group.id === SENSORI_GROUP_ID && group.selected === 1
				) &&
					state.sensorGroups.map((group, index) => (
						<SensorMarker
							group={group}
							key={index}
							handleMarkerClick={handleMarkerClick}
						/>
					))}

				{state.pinsGroups.some(
					(group) => group.id === USER_LOCATION_GROUP_ID && group.selected === 1
				) &&
					state.userLocation && (
						<Marker
							position={state.userLocation}
							icon={
								new L.Icon({
									iconUrl: "images/icons/youarehere.png",
									iconSize: [50, 50],
									iconAnchor: [25, 50],
									popupAnchor: [0, -50],
								})
							}
						>
							<Popup>
								<h3 className="text-lg font-semibold text-gray-800 text-center">
									La tua posizione
								</h3>
								<p className="text-gray-600 text-sm text-center mt-2">
									Questa è la tua posizione attuale rilevata dal dispositivo.
								</p>
							</Popup>
						</Marker>
					)}

				{state.pins &&
					state.pins.map((pin, index) => {
						const iconGroup = state.pinsGroups.find(
							(group) => group.id === pin.icon_group_id
						);
						if (!iconGroup || !iconGroup.selected) return null;
						return (
							<Marker
								key={index}
								id={"pin-" + index}
								aria-label={pin.title}
								position={[pin.lat, pin.lon]}
								icon={
									new L.Icon({
										iconUrl: pin.icon_path,
										iconSize: pin.icon_size.split(","),
										// iconAnchor: pin.icon_anchor.split(","),
										// popupAnchor: [0, 0],
									})
								}
							>
								<Popup>
									<PinPopup
										pin={pin}
										user={user}
										canEdit={canEdit}
										onEdit={() => editPin(pin)}
										onDelete={() =>
											dispatch({
												type: ACTIONS.SET_PIN_TO_DELETE,
												payload: pin.id,
											})
										}
									/>
								</Popup>
							</Marker>
						);
					})}

				<FilterBar canEdit={canEdit} isMonitor={isMonitor} />
				<LogoOverlay />
				{state.selectedSensor && <SensorGraphOverlay />}
				{user && canEdit && state.addPinEnabled && <MapClickHandler />}
				{user && canEdit && !state.addPinEnabled && <MapClickHandlerSimple />}
			</MapContainer>

			{state.modalOpen && (
				<AddPinModal pin={state.pendingPin} onSave={savePin} onCancel={cancelPin} />
			)}
			{state.pinToDelete && (
				<ConfirmModal
					message="Sei sicuro di voler rimuovere questo pin?"
					onConfirm={confirmRemovePin}
					onCancel={() => dispatch({ type: ACTIONS.SET_PIN_TO_DELETE, payload: null })}
				/>
			)}
		</div>
	);
}
